import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";

/**
 * Viewport-centered modal, portaled outside animated/scrolling page containers.
 * Long forms scroll inside the dialog, keeping its header and footer visible.
 */
const widthMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
};

export default function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  maxWidth = "md",
  testid = "modal",
}) {
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    });
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop — covers whole viewport including sidebar */}
      <div
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid={`${testid}-overlay`}
      />

      {/* Center relative to the viewport, regardless of the page scroll position. */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`relative w-full ${widthMap[maxWidth]} max-h-[calc(100dvh-2rem)] flex flex-col
                      bg-zinc-950/95 backdrop-blur-xl rounded-xl border border-zinc-800 overflow-hidden
                      shadow-[0_0_80px_-10px_rgba(0,0,0,0.8)] fade-up
                      pointer-events-auto`}
          data-testid={testid}
          aria-modal="true"
          role="dialog"
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-zinc-800/80">
            <div className="min-w-0">
              {eyebrow && (
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {eyebrow}
                </div>
              )}
              <h3 className="text-lg font-semibold tracking-tight mt-0.5 truncate">
                {title}
              </h3>
            </div>
            <button
              onClick={onClose}
              data-testid={`${testid}-close`}
              className="text-zinc-500 hover:text-zinc-100 transition-colors p-1 -mt-1 -mr-1 flex-shrink-0"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body — scrolls */}
          <div
            ref={bodyRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-5"
          >
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex-shrink-0 px-6 py-4 border-t border-zinc-800/80 bg-zinc-950/60">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
